import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../api';
import Footer from '../components/Footer';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import styles from './HomePage.module.css';
import Navbar, { BrandIcon } from '../components/Navbar';
import Login from '../components/login';
import Signup from '../components/login/Signup';

/* ── SVG Icons ─────────────────────────────────────── */
const IconZap = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
  </svg>
);
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
  </svg>
);
const IconX = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" />
  </svg>
);
const IconCheck = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" />
  </svg>
);
const IconShield = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
  </svg>
);
const IconDollar = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" x2="12" y1="2" y2="22" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);
const IconClock = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
  </svg>
);
const IconAlert = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
    <path d="M12 9v4" /><path d="M12 17h.01" />
  </svg>
);

/* ── Star Row ───────────────────────────────────────── */
const Stars = ({ count }) => (
  <span className={styles.stars}>
    {[1,2,3,4,5].map(i => (
      <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill={i <= count ? '#fbbf24' : 'none'} stroke={i <= count ? '#fbbf24' : '#d1d5db'} strokeWidth="2" aria-hidden="true">
        <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
      </svg>
    ))}
  </span>
);

/* ── FAQ Item ───────────────────────────────────────── */
const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.faqItem}>
      <button className={styles.faqQuestion} onClick={() => setOpen(o => !o)} aria-expanded={open}>
        {question}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: '#9219a6' }}>
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && <p className={styles.faqAnswer}>{answer}</p>}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════ */
const HomePage = () => {
  const { t, language } = useLanguage();
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [pricingMode, setPricingMode] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navUserRef = useRef(null);

  const [pricingError, setPricingError] = useState(false);
  const navigate = useNavigate();

  // Captures a "come back here after you sign in" request from a protected page
  // (e.g. OnboardingPage redirects here as /?login=true&redirect=/onboarding&auditRequestId=...
  // when it finds no logged-in user). Read before we strip the query string.
  const [pendingRedirect, setPendingRedirect] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('login') === 'true') {
      setShowLogin(true);
      const redirectPath = params.get('redirect');
      if (redirectPath) {
        setPendingRedirect({
          path: redirectPath,
          auditRequestId: params.get('auditRequestId') || null,
        });
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Shared post-auth handler for both the Login and Signup modals. If the user
  // was bounced here from a protected page, send them back to it now that
  // they're authenticated; otherwise fall back to each modal's own default.
  const handleAuthSuccess = (defaultPath) => {
    setIsLoggedIn(true);
    const target = pendingRedirect ? pendingRedirect.path : defaultPath;
    if (target) {
      navigate(target, { state: { auditRequestId: pendingRedirect?.auditRequestId || null } });
    }
  };

  useEffect(() => {
    const stored = localStorage.getItem('user1');
    setIsLoggedIn(!!localStorage.getItem('token1'));
    setUser(stored ? JSON.parse(stored) : null);
  }, []);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === 'token1' || event.key === 'user1') {
        setIsLoggedIn(!!localStorage.getItem('token1'));
        const stored = localStorage.getItem('user1');
        setUser(stored ? JSON.parse(stored) : null);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navUserRef.current && !navUserRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token1');
    localStorage.removeItem('user1');
    setIsLoggedIn(false);
    setUser(null);
    setShowUserMenu(false);
    navigate('/');
  };

  React.useEffect(() => {
    const loadPricing = async () => {
      try {
        const res = await fetch(getApiUrl('/api/pricing'));
        if (!res.ok) {
          console.error('Pricing fetch failed with status', res.status);
          setPricingError(true);
          return;
        }
        const data = await res.json();
        setPlans(data.packages || []);
      } catch (err) {
        console.error('Pricing fetch error', err);
        setPricingError(true);
      }
    };
    loadPricing();
  }, []);

  const packageDetails = {
    Starter: {
      desc: t('pkgStarterDesc', 'Excellent for new local businesses establishing their brand.'),
      features: [
        t('pkgStarterF1', 'Up to 10 crafted responses monthly'),
        t('pkgStarterF2', 'Email-based approval system'),
        t('pkgStarterF3', '24-hour delivery promise'),
        t('pkgStarterF4', 'Aligned brand voice profile')
      ],
    },
    Pro: {
      desc: t('pkgProDesc', 'Perfect for growing brands aiming to scale their reputation.'),
      features: [
        t('pkgProF1', 'Up to 40 crafted responses monthly'),
        t('pkgProF2', 'Email-based approval system'),
        t('pkgProF3', '24-hour delivery promise'),
        t('pkgProF4', 'Aligned brand voice profile'),
        t('pkgProF5', 'Priority response for critical feedback'),
        t('pkgProF6', 'Comprehensive monthly analytics report')
      ],
      featured: true,
    },
    'Ultr Pro': {
      desc: t('pkgUltraDesc', 'Tailored for busy locations and multi-unit brands.'),
      features: [
        t('pkgUltraF1', 'Unlimited crafted responses monthly'),
        t('pkgUltraF2', 'Email-based approval system'),
        t('pkgUltraF3', 'Express 12-hour response window'),
        t('pkgUltraF4', 'Aligned brand voice profile'),
        t('pkgUltraF5', 'Priority response for critical feedback'),
        t('pkgUltraF6', 'Weekly analytics & insight reports'),
        t('pkgUltraF7', 'Dedicated personal reputation specialist')
      ],
    },
  };

  const fallbackPlans = [
    { name: 'Starter', paymentMethod: { monthly: 0, annually: 0 } },
    { name: 'Pro', paymentMethod: { monthly: 5, annually: 4 } },
    { name: 'Ultr Pro', paymentMethod: { monthly: 6, annually: 7 } },
  ];

  const plansToRender = plans.length > 0 ? plans : fallbackPlans;

  const formatPrice = (paymentMethod) => {
    if (!paymentMethod) return '$0';
    return pricingMode === 'annually' ? `$${paymentMethod.annually}` : `$${paymentMethod.monthly}`;
  };

  return (
    <div>
      {/* ── NAVBAR ─────────────────────────────────────── */}
      <Navbar className={styles.navbar} innerClassName={styles.navInner} brandClassName={styles.navBrand} brand={<><BrandIcon size={24} style={{ marginRight: 6 }} />ReplyShoot</>}>
          <div className={styles.navLinks}>
            {/* <a href="#proof" className={styles.navLink}>{t('results', 'Results')}</a> */}
            <a href="#how-it-works" className={styles.navLink}>{t('howItWorks', 'How it Works')}</a>
            <a href="#pricing" className={styles.navLink}>{t('pricing', 'Pricing')}</a>
            <Link to="/tools" className={styles.navLink}>{t('tools', 'Tools')}</Link>
            {isLoggedIn && <Link to="/dashboard" className={styles.navLink}>{t('dashboard', 'Dashboard')}</Link>}
            
            {/* Header Language Switcher Toggle */}
            <LanguageToggle />

            {!isLoggedIn ? (
              <>
                <button 
                  onClick={() => setShowLogin(!showLogin)} 
                  className={styles.btnPrimary} 
                  style={{ padding: '8px 18px', borderRadius: '10px', fontSize: '0.85rem', boxShadow: 'none', border: 'none', cursor: 'pointer', background: '#f3f4f6', color: '#1f2937' }}
                >
                  {t('login', 'Login')}
                </button>
                <button 
                  onClick={() => setShowSignup(true)} 
                  className={styles.btnPrimary} 
                  style={{ padding: '8px 18px', borderRadius: '10px', fontSize: '0.85rem', boxShadow: 'none', border: 'none', cursor: 'pointer', marginLeft: 8 }}
                >
                  {t('signUp', 'Sign up')}
                </button>
              </>
            ) : (
              <div className={styles.navUser} ref={navUserRef}>
                <button
                  className={styles.navUserBtn}
                  onClick={() => setShowUserMenu(m => !m)}
                  aria-haspopup="true"
                  aria-expanded={showUserMenu}
                >
                  <span className={styles.navAvatar}>
                    {(user?.name || user?.email || 'U')[0].toUpperCase()}
                  </span>
                  <span className={styles.navUserName}>
                    {user?.name || user?.email || t('account', 'Account')}
                  </span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6, flexShrink: 0, transform: showUserMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
                </button>
                {showUserMenu && (
                  <div className={styles.navDropdown}>
                    <div className={styles.navDropdownHeader}>
                      <span className={styles.navDropdownAvatar}>
                        {(user?.name || user?.email || 'U')[0].toUpperCase()}
                      </span>
                      <div>
                        <div className={styles.navDropdownName}>{user?.name || t('account', 'Account')}</div>
                        <div className={styles.navDropdownEmail}>{user?.email || ''}</div>
                      </div>
                    </div>
                    <div className={styles.navDropdownDivider} />
                    <Link to="/dashboard" className={styles.navDropdownItem} onClick={() => setShowUserMenu(false)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                      {t('dashboard', 'Dashboard')}
                    </Link>
                    <button className={`${styles.navDropdownItem} ${styles.navDropdownLogout}`} onClick={handleLogout}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      {t('logout', 'Logout')}
                    </button>
                  </div>
                )}
              </div>
            )}
            <Link to="/audit" className={styles.btnPrimary} style={{ padding: '8px 14px', borderRadius: '10px', fontSize: '0.85rem', boxShadow: 'none' }}>
              {t('freeAudit', 'Free Audit')}
            </Link>
          </div>
      </Navbar>

      {/* ── LOGIN MODAL ────────────────────────────────── */}
      {showLogin && (
        <div className={styles.loginModal}>
          <div className={styles.loginModalContent}>
            <Login onClose={() => setShowLogin(false)} onAuth={() => handleAuthSuccess('/dashboard')} />
          </div>
        </div>
      )}

      {showSignup && (
        <div className={styles.loginModal}>
          <div className={styles.loginModalContent}>
            <Signup onClose={() => setShowSignup(false)} onAuth={() => handleAuthSuccess(null)} />
          </div>
        </div>
      )}

    {/* ── HERO ───────────────────────────────────────── */}
    <section className={styles.hero}>
      <div className="container">
        <div className={styles.heroTag}>
          <IconZap /> {t('heroBannerTag', 'AI-drafted replies for every Google review — approved by you before they post')}
        </div>
        <h1 className={styles.heroTitle}>
          {t('heroTitleLine1', 'Automate your Google Business reviews')}<br />
          <span className={styles.heroAccent}>{t('heroTitleAccent', 'now')}</span>
        </h1>
        <p className={styles.heroSub}>
          {t('heroSub', "ReplyShoot uses AI to draft a personalized reply to every review your business receives, usually within 24 hours. You preview each one and approve it by email before it's posted.")}
          <strong>{t('heroSubBold', ' You run your business, we handle the replies.')}</strong>
        </p>
        <Link to="/audit" className={styles.btnPrimary}>
          {t('heroCta', 'See Your Unanswered Reviews')} <IconArrow />
        </Link>
        <p className={styles.heroNote}>{t('heroNote', 'Free to check your reviews. No credit card required.')}</p>

        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <span className={styles.statNum}>500+</span>
            <span className={styles.statLabel}>{t('statLocalBrands', 'local brands analyzed')}</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statNum}>15K+</span>
            <span className={styles.statLabel}>{t('statReviewsProcessed', 'customer reviews processed')}</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statNum}>
              <IconShield size={32} style={{ color: '#c44ed0' }} />
            </span>
            <span className={styles.statLabel}>{t('statHipaaCompliant', '100% HIPAA-compliant')}<br /><small style={{ opacity: 0.6 }}>{t('statWorkflowsClinics', 'workflows for clinics')}</small></span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statNum}>
              <IconCheck size={32} style={{ color: '#c44ed0' }} />
            </span>
            <span className={styles.statLabel}>{t('statZeroLockIn', 'Zero lock-in contracts')}<br /><small style={{ opacity: 0.6 }}>{t('statCancelAnytime', 'cancel at any time')}</small></span>
          </div>
        </div>
      </div>
    </section>

    {/* ── STATS BAR ──────────────────────────────────── */}
    <section className={styles.statsBar}>
      <div className="container">
        <div className={styles.statsBarInner}>
          {[
            { num: '75%', label: t('statsBar1', 'of local companies ignore critical online complaints') },
            { num: '35%', label: t('statsBar2', 'average increase in sales with proactive reputation tracking') },
            { num: '88%', label: t('statsBar3', 'of shoppers favor local brands that actively reply to reviewers') },
            { num: '98%', label: t('statsBar4', 'of buyers search for local ratings before visiting a business') },
          ].map(s => (
            <div key={s.num}>
              <span className={styles.statsBarNum}>{s.num}</span>
              <span className={styles.statsBarLabel}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── PROBLEM ────────────────────────────────────── */}
    <section className={styles.problemSection}>
      <div className="container">
        <div className="text-center">
          <h2 className={styles.sectionTitle}>
            {t('problemTitle', 'The True Expense of')}{' '}
            <span className={styles.sectionAccent}>{t('problemTitleAccent', 'Unresolved Customer Feedback')}</span>
          </h2>
          <p className={styles.sectionSub}>{t('problemSub', 'Proven search data highlights the impact of leaving buyers in silence.')}</p>
        </div>
        <div className={styles.costGrid}>
          <div className={styles.costCard}>
            <div className={styles.costIcon}><IconX size={28} /></div>
            <p className={styles.costNum}>30</p>
            <p className={styles.costCardTitle}>{t('buyersLost', 'prospective buyers lost')}</p>
            <p className={styles.costCardSub}>{t('buyersLostSub', 'for each unanswered critical comment')}</p>
          </div>
          <div className={styles.costCard}>
            <div className={styles.costIcon}><IconClock size={28} /></div>
            <p className={styles.costNum}>8+ hrs</p>
            <p className={styles.costCardTitle}>{t('hoursSaved', 'productive hours saved monthly')}</p>
            <p className={styles.costCardSub}>{t('hoursSavedSub', 'by outsourcing individual response drafting')}</p>
          </div>
          <div className={styles.costCard}>
            <div className={styles.costIcon}><IconAlert size={28} /></div>
            <p className={styles.costNum}>63%</p>
            <p className={styles.costCardTitle}>{t('authorsUnanswered', 'of review authors')}</p>
            <p className={styles.costCardSub}>{t('authorsUnansweredSub', 'report never receiving any reply from local businesses')}</p>
          </div>
        </div>
        <div className={styles.mathBox}>
          <span className={styles.mathIcon}><IconDollar /></span>
          <div>
            <p className={styles.mathTitle}>{t('mathTitle', "Let's look at the numbers:")}</p>
            <p className={styles.mathText}>
              {t('mathText1', 'For example, if a customer is worth ')}<strong>$500 in lifetime value</strong>{t('mathText2', ' to your business, losing ')}<strong>30 prospective clients</strong>{t('mathText3', ' to unanswered reviews adds up to ')}
              <span className={styles.mathHighlight}>$15,000</span>{' '}
              {t('mathText4', 'in missed opportunity. ReplyShoot starts at just ')}<strong>$15/month</strong>.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* ── COMPARE ────────────────────────────────────── */}
    <section className={styles.compareSection}>
      <div className="container">
        <div className={styles.compareGrid}>
          <div className={`${styles.compareCard} ${styles.compareCardBad}`}>
            <div className={styles.compareCardHeader}>
              <span style={{ color: '#ef4444' }}><IconX size={20} /></span> {t('managingSolo', 'Managing Solo')}
            </div>
            <ul className={styles.compareList}>
              {[
                t('solo1', 'Unsatisfied reviews sit unresolved for weeks'),
                t('solo2', 'Shoppers assume customer support is non-existent'),
                t('solo3', 'Competitors who reply consistently make a stronger impression on new customers'),
                t('solo4', 'You waste valuable hours drafting individual replies by hand'),
                t('solo5', 'Uncontested 1-star ratings permanently damage your overall score'),
              ].map(tText => (
                <li key={tText} className={`${styles.compareItem} ${styles.compareItemBad}`}>
                  <span className={styles.badIcon}><IconX size={18} /></span> {tText}
                </li>
              ))}
            </ul>
          </div>
          <div className={`${styles.compareCard} ${styles.compareCardGood}`}>
            <div className={styles.compareCardHeader}>
              <span style={{ color: '#9219a6' }}><IconCheck size={20} /></span> {t('withReplyShoot', 'With ReplyShoot')}
            </div>
            <ul className={styles.compareList}>
              {[
                t('rs1', 'Every review receives a tailored, polite reply within 24 hours'),
                t('rs2', 'Prospective clients see an active, responsive local brand'),
                t('rs3', 'An active, responsive profile makes a stronger impression on potential customers'),
                t('rs4', 'You spend zero hours drafting — just approve via inbox'),
                t('rs5', 'Unhappy reviews are transformed into loyalty-building moments'),
              ].map(tText => (
                <li key={tText} className={`${styles.compareItem} ${styles.compareItemGood}`}>
                  <span className={styles.goodIcon}><IconCheck size={18} /></span> {tText}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* ── REVIEWS ────────────────────────────────────── */}
    <section id="proof" className={styles.reviewSection}>
      <div className="container">
        <div className="text-center">
          <h2 className={styles.sectionTitle}>
            {t('reviewsTitle', 'Real Examples of')} <span className={styles.sectionAccent}>{t('reviewsTitleAccent', 'Brand Replies We Craft')}</span>
          </h2>
          <p className={styles.sectionSub}>{t('reviewsSub', 'Personalized, context-aware replies drafted by AI and matched to your brand voice.')}</p>
        </div>
        <div style={{ marginTop: 40 }}>
          {[
            {
              biz: "Bella's Italian Kitchen", name: 'Karen M.', init: 'K', stars: 1,
              review: '"We waited almost an hour for our dinner. The entrees arrived cold, and they got our order mixed up. The staff seemed too busy to care. Extremely disappointing for a premium restaurant."',
              response: "Karen, I am incredibly sorry for this frustrating dinner experience. A long wait followed by cold, incorrect food is completely unacceptable on our part, and I take personal responsibility. We would love the chance to make this right — please contact us directly, as your next dinner is completely on us.",
              time: '2 hours',
            },
            {
              biz: 'Precision Plumbing Co.', name: 'Marcus T.', init: 'M', stars: 5,
              review: '"Excellent service from this plumbing team! They resolved our pipe leak in less than an hour at a very reasonable rate. Left the area clean and explained the fix. Highly recommended!"',
              response: "Marcus, thank you so much for your kind recommendation! Providing fast, tidy service at a transparent price is our daily mission. We appreciate you sharing your experience, and we are always here to help whenever you need assistance again.",
              time: '4 hours',
            },
            {
              biz: 'Smile Dental Group', name: 'Linda R.', init: 'L', stars: 2,
              review: '"The front reception team was not welcoming, and my appointment started 30 minutes behind schedule. The clinical care was good, but the administrative wait was very frustrating."',
              response: "Linda, we sincerely appreciate your honest feedback. You are absolutely right — long wait times and an unwelcoming reception do not reflect our commitment to care. We would appreciate another opportunity to show you the warm patient care we are known for.",
              time: '3 hours',
            },
          ].map(r => (
            <div key={r.name} className={styles.reviewCard}>
              <div className={styles.reviewTop}>
                <p className={styles.reviewBiz}>{r.biz}</p>
                <div className={styles.reviewerRow}>
                  <div className={styles.reviewerAvatar}>{r.init}</div>
                  <div>
                    <p className={styles.reviewerName}>{r.name}</p>
                    <Stars count={r.stars} />
                  </div>
                </div>
                <p className={styles.reviewText}>{r.review}</p>
              </div>
              <div className={styles.reviewResponse}>
                <div className={styles.responseHeader}>
                  <span className={styles.responseBy}>
                    <IconShield size={16} /> {t('ownerResponse', 'Owner Response — by ReplyShoot')}
                  </span>
                  <span className={styles.responseBadge}>{t('respondedIn', 'Responded in')} {r.time}</span>
                </div>
                <p className={styles.responseText}>{r.response}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-4">
          <a href="#pricing" className={styles.btnPrimary}>
            {t('getResponsesLikeThese', 'Get Responses Like These for Your Business')} <IconArrow />
          </a>
        </div>
      </div>
    </section>

    {/* ── HOW IT WORKS ───────────────────────────────── */}
    <section id="how-it-works" className={styles.howSection}>
      <div className="container">
        <div className="text-center mb-5">
          <h2 className={styles.sectionTitle}>
            {t('howTitle', 'You build your business.')} <span className={styles.sectionAccent}>{t('howTitleAccent', 'We curate your feedback.')}</span>
          </h2>
          <p className={styles.sectionSub}>
            {t('howSub', 'Quick 15-minute onboarding. Moving forward, you will spend less than 5 minutes a week verifying drafts.')}
          </p>
        </div>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          {[
            { num: '01', title: t('step1Title', 'Brand Voice Integration'), text: t('step1Text', 'We conduct a quick 15-minute setup call to align on your unique brand voice, guidelines, and core offerings. We write just like you.') },
            { num: '02', title: t('step2Title', 'Tailored Draft Generation'), text: t('step2Text', 'The moment a new review appears, our AI creates a personalized, context-aware reply within 24 hours, matched to your brand voice.') },
            { num: '03', title: t('step3Title', 'One-Click Inbox Approval'), text: t('step3Text', "You receive a clean email notification: review the drafted response and click 'Approve' to post. Nothing goes public without your explicit consent.") },
          ].map(s => (
            <div key={s.num} className={styles.howStep}>
              <div className={styles.howNum}>{s.num}</div>
              <div>
                <h3 className={styles.howTitle}>{s.title}</h3>
                <p className={styles.howText}>{s.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── VS TABLE ───────────────────────────────────── */}
    <section className={styles.vsSection}>
      <div className="container">
        <div className="text-center mb-5">
          <h2 className={styles.sectionTitle} style={{ color: '#fff' }}>
            {t('vsTitle', "Software Doesn't Do the Work.")} <span style={{ color: '#c084fc' }}>{t('vsTitleAccent', 'We Do.')}</span>
          </h2>
        </div>
        <div className={styles.vsGrid}>
          <div className={`${styles.vsCard} ${styles.vsCardBad}`}>
            <p className={`${styles.vsLabel} ${styles.vsLabelBad}`}>{t('vsSoftwareLabel', 'Subscription Software')}</p>
            <ul className={styles.vsList}>
              {[t('vsSoftware1', 'You must log in and write all replies yourself'), t('vsSoftware2', 'Yet another complex dashboard to master'), t('vsSoftware3', "Stiff, generic template replies that don't sound like you"), t('vsSoftware4', '$200 to $400 monthly for unused software')].map(item => (
                <li key={item} className={styles.vsItemBad}><span style={{ color: '#ef4444', flexShrink: 0, marginTop: 1 }}><IconX size={16} /></span>{item}</li>
              ))}
            </ul>
          </div>
          <div className={`${styles.vsCard} ${styles.vsCardGood}`}>
            <p className={`${styles.vsLabel} ${styles.vsLabelGood}`}>{t('vsRsLabel', 'ReplyShoot')}</p>
            <ul className={styles.vsList}>
              {[t('vsRs1', 'AI drafts every reply for you, matched to your brand voice'), t('vsRs2', 'Zero dashboards — one-click approval via email'), t('vsRs3', 'Highly personalized to fit your exact brand tone'), t('vsRs4', 'Starts at $15/mo for complete management')].map(item => (
                <li key={item} className={styles.vsItemGood}><span style={{ color: '#e9d5f7', flexShrink: 0, marginTop: 1 }}><IconCheck size={16} /></span>{item}</li>
              ))}
            </ul>
          </div>
          <div className={`${styles.vsCard} ${styles.vsCardBad}`}>
            <p className={`${styles.vsLabel} ${styles.vsLabelBad}`}>{t('vsDiyLabel', 'In-House DIY')}</p>
            <ul className={styles.vsList}>
              {[t('vsDiy1', 'Wastes 6 to 8 hours of premium owner time monthly'), t('vsDiy2', 'Highly inconsistent response times'), t('vsDiy3', 'Risk of writing emotional replies to bad reviews'), t('vsDiy4', 'Constantly pushed down your daily to-do list')].map(item => (
                <li key={item} className={styles.vsItemBad}><span style={{ color: '#ef4444', flexShrink: 0, marginTop: 1 }}><IconX size={16} /></span>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* ── PROOF STATS ────────────────────────────────── */}
    <section className={styles.proofSection}>
      <div className="container">
        <div className="text-center">
          <h2 className={styles.sectionTitle}>{t('proofTitle', 'Active Engagement')} <span className={styles.sectionAccent}>{t('proofTitleAccent', 'Drives Growth')}</span></h2>
        </div>
        <div className={styles.proofGrid}>
          {[
            { num: '35%', label: t('proof1Label', 'average sales growth for local brands responding to 25%+ of feedback'), src: 'Womply, 200K+ SMBs' },
            { num: '5–9%', label: t('proof2Label', 'increase in overall business revenue per 0.5 star rating improvement'), src: 'Harvard Business School' },
            { num: '88%', label: t('proof3Label', 'of local searchers choose brands that reply to every review'), src: 'BrightLocal 2024' },
            { num: '98%', label: t('proof4Label', 'of new buyers read rating profiles prior to making a purchase decision'), src: 'BrightLocal 2024' },
          ].map(p => (
            <div key={p.num} className={styles.proofCard}>
              <p className={styles.proofNum}>{p.num}</p>
              <p className={styles.proofLabel}>{p.label}</p>
              <p className={styles.proofSource}>{p.src}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── PRICING ────────────────────────────────────── */}
    <section id="pricing" className={styles.pricingSection}>
      <div className="container">
        <div className="text-center">
          <h2 className={styles.sectionTitle}>{t('pricingTitle', 'Clear, Straightforward')} <span className={styles.sectionAccent}>{t('pricingTitleAccent', 'Plans')}</span></h2>
          <p className={styles.sectionSub}>{t('pricingSub', 'No hidden fees. No long-term commitments. Scale or cancel instantly.')}</p>
        </div>
        <div className="d-flex justify-content-center mb-4">
          <div className="btn-group" role="group" aria-label="Pricing toggle">
            <button type="button" className={`btn ${pricingMode === 'monthly' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setPricingMode('monthly')}>
              {t('monthly', 'Monthly')}
            </button>
            <button type="button" className={`btn ${pricingMode === 'annually' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setPricingMode('annually')}>
              {t('annually', 'Annually')}
            </button>
          </div>
        </div>
        <div className={styles.pricingGrid}>
          {plansToRender.map((p) => {
            const details = packageDetails[p.name] || {};
            const price = formatPrice(p.paymentMethod);
            const per = pricingMode === 'annually' ? t('perYear', '/year') : t('perMonth', '/month');

            return (
              <div key={p.name} className={`${styles.pricingCard} ${details.featured ? styles.pricingCardFeatured : ''}`}>
                {details.featured && <span className={styles.pricingBadge}>{t('mostPopular', 'Most Popular')}</span>}
                <p className={styles.pricingName}>{p.name}</p>
                <p className={styles.pricingPrice}>{price}</p>
                <p className={styles.pricingPer}>{per}</p>
                <p className={styles.pricingDesc}>{details.desc || 'Pricing optimized for your business scale.'}</p>
                <ul className={styles.pricingFeatures}>
                  {(p.descriptionPoints && p.descriptionPoints.length > 0 ? p.descriptionPoints : (details.features || [])).map((f, fi) => {
                    const text = typeof f === 'string' ? f : (f[language] || f.en || f.ar || '');
                    return (
                      <li key={fi} className={styles.pricingFeatureItem}>
                        <span className={styles.pricingCheckIcon}><IconCheck size={16} /></span> {text}
                      </li>
                    );
                  })}
                </ul>
                <Link to="/onboarding" className={styles.btnPrimary} style={{ width: '100%', justifyContent: 'center' }}>
                  {t('getStarted', 'Get Started')} <IconArrow />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* ── FAQ ────────────────────────────────────────── */}
    <section className={styles.faqSection}>
      <div className="container">
        <div className="text-center">
          <h2 className={styles.sectionTitle}>{t('faqTitle', 'Frequently Asked')} <span className={styles.sectionAccent}>{t('faqTitleAccent', 'Questions')}</span></h2>
        </div>
        <div className={styles.faqList} style={{ maxWidth: 720, margin: '0 auto' }}>
          {[
            { q: t('faq1Q', 'Why shouldn\'t I just write replies myself?'), a: t('faq1A', "While you certainly can, most local business owners simply don't have the time to keep up consistently. Unresolved reviews build up quickly, and prospective buyers often go with whichever business responds first. We remove the operational headache entirely.") },
            { q: t('faq2Q', 'How do you capture my specific brand voice?'), a: t('faq2A', 'During onboarding, we align on your unique business values, vocabulary preferences, and tone guidelines. Since nothing is published without your direct one-click email approval, you retain complete control over every single word.') },
            { q: t('faq3Q', 'Is this service worth the monthly cost?'), a: t('faq3A', "Consider the value of a single customer. If keeping just one buyer from choosing a competitor covers your subscription, it's already paid for itself. Most owners find it one of the most time-effective ways to stay on top of their reviews.") },
            { q: t('faq4Q', 'What if I want to edit a drafted reply?'), a: t('faq4A', 'You have absolute authority. Each draft is delivered to your inbox, allowing you to easily request modifications or customize the text before it is published on your profile.') },
            { q: t('faq5Q', 'My business doesn\'t receive reviews very often.'), a: t('faq5A', 'If you have a low volume of reviews, each rating carries significantly more weight. A single unaddressed low rating can heavily impact your average star score. Promptly responding to each review protects your rating from fluctuations.') },
            { q: t('faq6Q', 'Why not just copy-paste from AI generators?'), a: t('faq6A', "Generic AI tools don't know your business, your brand voice, or the specific review they're replying to, so replies often come out sounding robotic and generic. ReplyShoot's AI is built specifically for review replies, and you still approve every single one before it's posted.") },
          ].map(f => <FaqItem key={f.q} question={f.q} answer={f.a} />)}
        </div>
      </div>
    </section>

    {/* ── CTA ────────────────────────────────────────── */}
    <section className={styles.ctaSection}>
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <h2 className={styles.ctaTitle}>{t('ctaTitle', 'Keep Your Review Profile Active.')}</h2>
        <p className={styles.ctaSub}>{t('ctaSub', 'Get a free, personalized review report for your business.')}</p>
        <Link to="/audit" className={styles.btnPrimary}>
          {t('scanReviewsNow', 'Scan My Business Reviews Now')} <IconArrow />
        </Link>
        <p className={styles.ctaNote}>{t('ctaNote', 'Free analysis. No payment details required.')}</p>
      </div>
    </section>

    {/* ── FOOTER ─────────────────────────────────────── */}
    <Footer />
    </div>
  );
};

export default HomePage;
